/**
 * Edition-level progress track (60% questionnaires / 40% other pages).
 * Mirrors SPA routing in Answer / AcknowledgeUser / categoryFlow + Léger integration.
 */

import { Category, Survey } from '@models/Survey'
import { SurveyStep } from '@models/Survey/SurveyStep'
import {
  categorySkipsPredictionStep,
  CategorySurveyRef,
  getCategory,
  sortedCategorySurveys,
} from '@utils/categoryFlow'

export const QUESTIONNAIRE_WIDTH_PCT = 60
export const PAGE_WIDTH_PCT = 40
/** Prediction segment is wider than other pages (room for gotchi icon). */
export const PREDICTION_PAGE_WEIGHT = 1.75
export const DEFAULT_PAGE_WEIGHT = 1

export type ProgressSegmentKind = 'page' | 'questionnaire'

export type ProgressPageId =
  | 'consent'
  | 'email'
  | 'acknowledge'
  | 'prediction'
  | 'rectify'
  | 'probability'
  | 'forecasting'
  | 'results'
  | 'sandbox'

export type ProgressSegmentStatus = 'upcoming' | 'current' | 'completed'

export type ProgressSegment = {
  id: string
  kind: ProgressSegmentKind
  pageId?: ProgressPageId
  surveyKey?: string
  surveyIndex?: number
  surveyCount?: number
  resultVariant?: 'intermediate' | 'final'
  widthPct: number
  fillRatio: number
  status: ProgressSegmentStatus
  /** Visible questions on the answered path (set by UI from live/frozen stats). */
  visibleQuestionCount?: number
  /** Total questions in survey including currently hidden conditionals. */
  totalQuestionCount?: number
}

export type EditionProgressInput = {
  category?: Category | null
  currentSurvey?: Survey | null
  step: SurveyStep
  hasConsented: boolean
  surveyIsCompleted: boolean
  currentPosition: number
  questionsCount: number
  isIntegrated: boolean
  hasVisitedRectify: boolean
  resultVariant?: 'intermediate' | 'final'
  /** Total questions per survey key — used to size questionnaire segments within the 60% band. */
  questionnaireLengthsByKey?: Record<string, number>
}

type RawSegment = {
  id: string
  kind: ProgressSegmentKind
  pageId?: ProgressPageId
  surveyKey?: string
  surveyIndex?: number
  surveyCount?: number
  resultVariant?: 'intermediate' | 'final'
}

export function getEditionSurveyRefs(
  category?: Category | null,
  currentSurvey?: Survey | null,
): CategorySurveyRef[] {
  const sorted = sortedCategorySurveys(category)
  if (sorted.length) {
    return sorted
  }
  if (currentSurvey) {
    return [
      {
        id: currentSurvey.id,
        key: currentSurvey.key,
        order: currentSurvey.order ?? 0,
      },
    ]
  }
  return []
}

function surveyStubFromRef(ref: CategorySurveyRef, category: Category | null): Survey {
  return {
    id: ref.id,
    key: ref.key,
    name: ref.key,
    order: ref.order,
    active: true,
    version: 1,
    graphics: [],
    category: category ?? undefined,
  }
}

/** Ordered raw segments (no widths / fill). */
export function listEditionProgressRawSegments(input: {
  category?: Category | null
  currentSurvey?: Survey | null
  isIntegrated: boolean
  hasVisitedRectify: boolean
}): RawSegment[] {
  const category = input.category ?? getCategory(input.currentSurvey) ?? null
  const surveys = getEditionSurveyRefs(category, input.currentSurvey)
  const n = surveys.length
  const segments: RawSegment[] = []

  segments.push({ id: 'consent', kind: 'page', pageId: 'consent' })

  surveys.forEach((ref, i) => {
    segments.push({
      id: `questionnaire:${ref.key}`,
      kind: 'questionnaire',
      surveyKey: ref.key,
      surveyIndex: i,
      surveyCount: n,
    })

    if (i === 0) {
      if (!input.isIntegrated) {
        segments.push({ id: 'email', kind: 'page', pageId: 'email' })
      }
      segments.push({ id: 'acknowledge', kind: 'page', pageId: 'acknowledge' })
    }

    const isLast = i === n - 1
    if (!isLast) {
      segments.push({
        id: `results:intermediate:${ref.key}`,
        kind: 'page',
        pageId: 'results',
        surveyKey: ref.key,
        resultVariant: 'intermediate',
      })
      return
    }

    const lastSurvey = surveyStubFromRef(ref, category)
    const skipTunnel = categorySkipsPredictionStep(category, lastSurvey)
    if (!skipTunnel) {
      segments.push({ id: 'prediction', kind: 'page', pageId: 'prediction' })
      if (input.hasVisitedRectify) {
        segments.push({ id: 'rectify', kind: 'page', pageId: 'rectify' })
      }
      segments.push({ id: 'probability', kind: 'page', pageId: 'probability' })
      segments.push({ id: 'forecasting', kind: 'page', pageId: 'forecasting' })
    }

    segments.push({
      id: 'results:final',
      kind: 'page',
      pageId: 'results',
      resultVariant: 'final',
    })

    if (!input.isIntegrated) {
      segments.push({ id: 'sandbox', kind: 'page', pageId: 'sandbox' })
    }
  })

  return segments
}

export function resolveCurrentProgressSegmentId(input: EditionProgressInput): string {
  const { step, currentSurvey, resultVariant, hasConsented } = input
  const surveyKey = currentSurvey?.key

  if (!hasConsented) {
    return 'consent'
  }

  switch (step) {
    case SurveyStep.Answer:
      return surveyKey ? `questionnaire:${surveyKey}` : 'consent'
    case SurveyStep.Email:
      return 'email'
    case SurveyStep.Acknowledge:
      return 'acknowledge'
    case SurveyStep.Prediction:
      return 'prediction'
    case SurveyStep.Rectify:
      return 'rectify'
    case SurveyStep.Probability:
      return 'probability'
    case SurveyStep.Forecasting:
      return 'forecasting'
    case SurveyStep.ResultsIntermediate:
      return surveyKey ? `results:intermediate:${surveyKey}` : 'results:final'
    case SurveyStep.Results:
      if (resultVariant === 'intermediate' && surveyKey) {
        return `results:intermediate:${surveyKey}`
      }
      return 'results:final'
    case SurveyStep.Sandbox:
      return 'sandbox'
    default:
      return surveyKey ? `questionnaire:${surveyKey}` : 'consent'
  }
}

function questionnaireFillRatio(input: EditionProgressInput, isCurrent: boolean, isCompleted: boolean): number {
  if (isCompleted) {
    return 1
  }
  if (!isCurrent) {
    return 0
  }
  const total = input.questionsCount
  if (!total || total <= 0) {
    return input.surveyIsCompleted ? 1 : 0
  }
  const pos = Math.min(Math.max(input.currentPosition || 0, 0), total)
  if (input.surveyIsCompleted) {
    return 1
  }
  return pos / total
}

function pageSegmentWeight(pageId?: ProgressPageId): number {
  if (pageId === 'prediction') {
    return PREDICTION_PAGE_WEIGHT
  }
  return DEFAULT_PAGE_WEIGHT
}

function questionnaireSegmentWeight(
  seg: RawSegment,
  lengthsByKey?: Record<string, number>,
): number {
  if (!seg.surveyKey) {
    return 1
  }
  const n = lengthsByKey?.[seg.surveyKey]
  return n != null && n > 0 ? n : 1
}

export function buildEditionProgressSegments(input: EditionProgressInput): ProgressSegment[] {
  const category = input.category ?? getCategory(input.currentSurvey) ?? null
  const raw = listEditionProgressRawSegments({
    category,
    currentSurvey: input.currentSurvey,
    isIntegrated: input.isIntegrated,
    hasVisitedRectify: input.hasVisitedRectify || input.step === SurveyStep.Rectify,
  })

  const qSegs = raw.filter((s) => s.kind === 'questionnaire')
  const pageSegs = raw.filter((s) => s.kind === 'page')
  const qWeightTotal =
    qSegs.reduce((sum, s) => sum + questionnaireSegmentWeight(s, input.questionnaireLengthsByKey), 0) || 1
  const pageWeightTotal = pageSegs.reduce((sum, s) => sum + pageSegmentWeight(s.pageId), 0) || 1

  const currentId = resolveCurrentProgressSegmentId({
    ...input,
    hasVisitedRectify: input.hasVisitedRectify || input.step === SurveyStep.Rectify,
  })
  let currentIndex = raw.findIndex((s) => s.id === currentId)
  if (currentIndex < 0) {
    // e.g. email segment absent in integration — fall back to acknowledge / questionnaire
    if (currentId === 'email') {
      currentIndex = raw.findIndex((s) => s.id === 'acknowledge')
    } else if (currentId === 'rectify') {
      currentIndex = raw.findIndex((s) => s.id === 'probability')
    } else if (currentId.startsWith('results:intermediate:')) {
      currentIndex = raw.findIndex((s) => s.id === 'results:final')
    }
  }
  if (currentIndex < 0) {
    currentIndex = 0
  }

  return raw.map((seg, index) => {
    const widthPct =
      seg.kind === 'questionnaire'
        ? (QUESTIONNAIRE_WIDTH_PCT * questionnaireSegmentWeight(seg, input.questionnaireLengthsByKey)) /
          qWeightTotal
        : (PAGE_WIDTH_PCT * pageSegmentWeight(seg.pageId)) / pageWeightTotal
    let status: ProgressSegmentStatus = 'upcoming'
    let fillRatio = 0

    if (index < currentIndex) {
      status = 'completed'
      fillRatio = 1
    } else if (index > currentIndex) {
      status = 'upcoming'
      fillRatio = 0
    } else {
      status = 'current'
      if (seg.kind === 'questionnaire') {
        fillRatio = questionnaireFillRatio(input, true, false)
        if (fillRatio >= 1) {
          status = 'completed'
        }
      } else {
        // Page currently displayed: treat as in-progress (half) unless already "done" semantics
        // Consent / pages: show as current with full fill once reached (user is on the page).
        fillRatio = 1
      }
    }

    return {
      ...seg,
      widthPct,
      fillRatio,
      status,
    }
  })
}
